import { useEffect, useRef, useState } from "react";
import { Loader2, X } from "lucide-react";
import { loadNatures, searchNatures } from "@/data/nature";

// Nature of Business: type any word, choose from the SIC 7 activities that contain it (owner: picked
// from the list only - what is typed is just the search)
const NatureSearch = ({ id, value, onChange, onBlur, invalid }) => {
  const [list, setList] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const listRef = useRef(null);

  const ensureList = () => {
    if (list) return;
    loadNatures().then(setList).catch((err) => setLoadError(err.message));
  };

  const results = list && query ? searchNatures(list, query) : [];

  useEffect(() => {
    setActive(0);
  }, [query]);

  useEffect(() => {
    listRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: "nearest" });
  }, [active]);

  const pick = (e) => {
    onChange(e.text);
    setQuery("");
    setOpen(false);
  };

  const keyDown = (ev) => {
    if (!open || !results.length) return;
    if (ev.key === "ArrowDown") {
      ev.preventDefault();
      setActive((a) => Math.min(a + 1, results.length - 1));
    } else if (ev.key === "ArrowUp") {
      ev.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (ev.key === "Enter") {
      ev.preventDefault();
      pick(results[active]);
    } else if (ev.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="bee-nature">
      <div className="bee-nature-box">
        <input
          id={id}
          className={`bee-input bee-nature-input${invalid ? " is-invalid" : ""}${value ? " is-chosen" : ""}`}
          value={value || query}
          placeholder="Type a word, e.g. plumber, attorney, retail, farming"
          autoComplete="off"
          role="combobox"
          aria-expanded={open && results.length > 0}
          aria-controls={`${id}-list`}
          aria-autocomplete="list"
          onFocus={() => { ensureList(); if (query) setOpen(true); }}
          onChange={(ev) => {
            if (value) onChange("");
            setQuery(ev.target.value);
            setOpen(true);
            ensureList();
          }}
          onKeyDown={keyDown}
          onBlur={() => { setOpen(false); onBlur?.(); }}
          data-testid="input-nature_of_business"
        />
        {(value || query) && (
          <button
            type="button"
            className="bee-nature-clear"
            onMouseDown={(ev) => ev.preventDefault()}
            onClick={() => { onChange(""); setQuery(""); document.getElementById(id)?.focus(); }}
            aria-label="Clear the Nature of Business"
          >
            <X size={15} />
          </button>
        )}
      </div>
      {open && query && (
        <div className="bee-nature-pop" ref={listRef} id={`${id}-list`} role="listbox" data-testid="nature-results">
          {loadError ? (
            <p className="bee-nature-empty is-error">{loadError}</p>
          ) : !list ? (
            <p className="bee-nature-empty"><Loader2 size={15} className="bee-spin" /> Loading the list...</p>
          ) : results.length === 0 ? (
            <p className="bee-nature-empty">Nothing found for "{query}". Try another word.</p>
          ) : (
            results.map((e, i) => (
              <button
                type="button"
                key={e.code}
                data-index={i}
                role="option"
                aria-selected={i === active}
                className={`bee-nature-option${i === active ? " is-active" : ""}`}
                onMouseDown={(ev) => ev.preventDefault()}
                onMouseEnter={() => setActive(i)}
                onClick={() => pick(e)}
              >
                <span>{e.text}</span>
                <span className="bee-nature-code">SIC {e.code}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default NatureSearch;
